import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useVirtualizer} from '@tanstack/react-virtual';
import useTranslation from 'next-translate/useTranslation';
import {twMerge} from 'tailwind-merge';
import {Temporal} from 'temporal-polyfill';

import {FulfillmentDetailStrategy} from '@/graphql/types';
import {useEventBarV2} from '../../EventBarV2Provider';
import {getAnchorIndex, getVisibleTimeItems} from '../../utils/when';

const ROW_HEIGHT = 40;

type WhenTimeListProps = {
  fulfillmentType: FulfillmentDetailStrategy;
  idSuffix: string;
  isCompressed: boolean;
  isInvalid: (time: Temporal.PlainTime) => boolean;
  onSelect: (time: string | null, event: React.UIEvent) => void;
  value: string | null;
};

const WhenTimeList: React.FC<WhenTimeListProps> = ({
  fulfillmentType,
  idSuffix,
  isCompressed,
  isInvalid,
  onSelect,
  value,
}) => {
  const {t} = useTranslation('app-bar');
  const {timeItemsCompressed, timeItemsExpanded, whenDate} = useEventBarV2();

  const headerId = `when-time-list-header-${idSuffix}`;
  const isPickup = fulfillmentType === FulfillmentDetailStrategy.Takeout;
  const timeItems = isCompressed ? timeItemsCompressed : timeItemsExpanded;

  const visibleItems = getVisibleTimeItems(timeItems, isInvalid);
  const anchorIndex = getAnchorIndex(visibleItems, value);

  const [activeIndex, setActiveIndex] = useState(anchorIndex);

  // Snap the active option back to the anchor whenever the date or anchor moves (picking a date
  // refilters the list and recomputes the anchor).
  const resetKey = `${whenDate}#${anchorIndex}`;
  const [lastResetKey, setLastResetKey] = useState(resetKey);
  if (lastResetKey !== resetKey) {
    setLastResetKey(resetKey);
    setActiveIndex(anchorIndex);
  }

  const scrollRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual manages its own state; React Compiler skips it automatically.
  const virtualizer = useVirtualizer({
    count: visibleItems.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 6,
  });

  // Center on the selected slot (or noon) when the panel opens, a date is picked, or a time is
  // selected — but not when a time is *un*selected
  const prevValueRef = useRef(value);
  useLayoutEffect(() => {
    const wasUnselected = prevValueRef.current !== null && value === null;
    prevValueRef.current = value;
    if (visibleItems.length === 0 || wasUnselected) return;
    virtualizer.scrollToIndex(Math.min(anchorIndex, visibleItems.length - 1), {
      align: 'center',
      behavior: 'smooth',
    });
  }, [anchorIndex, value, virtualizer, visibleItems.length, whenDate]);

  // Keep the active option in view as the user arrows through the virtualized list.
  useEffect(() => {
    if (visibleItems.length === 0 || activeIndex === anchorIndex) return;
    virtualizer.scrollToIndex(Math.min(activeIndex, visibleItems.length - 1), {align: 'auto'});
  }, [activeIndex, anchorIndex, virtualizer, visibleItems.length]);

  // Clamp the active option back into range when the list shrinks.
  useEffect(() => {
    if (visibleItems.length > 0 && activeIndex >= visibleItems.length) {
      setActiveIndex(visibleItems.length - 1);
    }
  }, [activeIndex, visibleItems.length]);

  const selectByIndex = (index: number, event: React.UIEvent) => {
    const item = visibleItems[index];
    if (!item) return;
    // Re-selecting the active choice clears it (mirrors the old radio deselect-on-reclick).
    onSelect(item.optionValue === value ? null : item.optionValue, event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const last = visibleItems.length - 1;
    if (last < 0) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex(index => Math.min(index + 1, last));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex(index => Math.max(index - 1, 0));
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(last);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        selectByIndex(activeIndex, event);
        break;
      default:
        break;
    }
  };

  // The list is virtualized, so the active row may be scrolled out of the DOM. Only point
  // aria-activedescendant at it while it is actually rendered — a dangling reference is invalid ARIA.
  const virtualItems = virtualizer.getVirtualItems();
  const isActiveRendered = virtualItems.some(virtualRow => virtualRow.index === activeIndex);
  const activeOptionId = isActiveRendered ? visibleItems[activeIndex]?.optionId : undefined;

  return (
    <div className="absolute inset-0 flex flex-col rounded-xl border border-peppercorn-100 p-2">
      <div className="px-2 pb-2 text-sm font-semibold text-peppercorn-500" id={headerId}>
        {isPickup ? t('eventBarV2.when.pickupTime') : t('eventBarV2.when.deliveryTime')}
      </div>
      {visibleItems.length === 0 ? (
        <div
          className="flex min-h-0 flex-1 items-center justify-center px-2 text-sm text-peppercorn-400"
          data-testid="when-time-list-empty"
          role="status"
        >
          {t('eventBarV2.when.noTimes')}
        </div>
      ) : (
        <div
          aria-activedescendant={activeOptionId}
          aria-labelledby={headerId}
          className="min-h-0 flex-1 overflow-y-auto rounded-xl p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-300"
          data-testid="when-time-list"
          onKeyDown={handleKeyDown}
          ref={scrollRef}
          role="listbox"
          tabIndex={0}
        >
          <div className="relative w-full" style={{height: `${virtualizer.getTotalSize()}px`}}>
            {virtualItems.map(virtualRow => {
              const item = visibleItems[virtualRow.index];
              const isSelected = item.optionValue === value;
              const isActive = virtualRow.index === activeIndex;

              return (
                // Keyboard is handled on the listbox container via aria-activedescendant, so the
                // options do not need their own key handlers or tabIndex — focus stays on the
                // listbox div which manages keyboard navigation for all children.
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
                <div
                  aria-selected={isSelected}
                  className={twMerge(
                    'absolute left-0 top-0 flex w-full cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-peppercorn-50',
                    isActive && 'bg-peppercorn-50',
                  )}
                  data-testid={`when-time-option-${item.safeValue}`}
                  id={item.optionId}
                  key={item.optionValue}
                  onClick={event => {
                    setActiveIndex(virtualRow.index);
                    selectByIndex(virtualRow.index, event);
                  }}
                  role="option"
                  style={{
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <span
                    aria-hidden
                    className={twMerge(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-peppercorn-300',
                      isSelected && 'border-green-700',
                    )}
                  >
                    {isSelected && <span className="h-2 w-2 rounded-full bg-green-700" />}
                  </span>
                  {item.label}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WhenTimeList;
